import { h, Fragment } from "preact";
import { RoutableProps } from "preact-router";
import { useCallback, useRef, useState } from "preact/hooks";
import "./styles.scss";
import { utilsEvent as utilsEventUrl } from "../../../api";
import { Alert } from "../../../components/Alert";
import { useEventSource } from "../../../hooks";
import { egzotechAgentManager, route } from "../../../App";
import { parse } from "ansicolor";

export function SidraLEGTabletFlash(props: RoutableProps & { exitDestination?: string }) {
    const [flashStatus, setFlashStatus] = useState("init" as "init" | "programming" | "done" | "error");
    const [checkStatus, setCheckStatus] = useState("init" as "init" | "checking" | "done" | "error");
    const [flashConsoleOutput, setFlashConsoleOutput] = useState("");
    const [checkConsoleOutput, setCheckConsoleOutput] = useState("");
    const [flashStart, setFlashStart] = useState(false);
    const [checkStart, setCheckStart] = useState(false);
    const flashConsoleOutputRef = useRef<HTMLDivElement>(null);
    const checkConsoleOutputRef = useRef<HTMLDivElement>(null);

    const flashTablet = useCallback(() => {
        setFlashStatus("programming");
        setFlashStart(true);
    }, [true]);

    const checkTablet = useCallback(() => {
        setCheckStatus("checking");
        setCheckStart(true);
    }, [true]);

    useEventSource<{ hasErrors: boolean, output: string }>(utilsEventUrl(egzotechAgentManager.getAgentWithModule("yocto-flash")!, "yocto-flash"), flashStart, {
        onOpen: (userdata) => {
            userdata.hasErrors = false;
            userdata.output = "";
            setFlashConsoleOutput(userdata.output);

            setFlashStatus("programming");
        },
        onMessage: (data, userdata) => {
            let output = userdata.output + data.replace(/(\\n)/gm, "\n").replace(/(\\r)/gm, "\r");
            output = output.replace(/\r[^\n\r]+\r/, '\r');
            userdata.output = output;
            setFlashConsoleOutput(userdata.output);

            setTimeout(() => {
                if (flashConsoleOutputRef.current) {
                    flashConsoleOutputRef.current.scrollTo(0, flashConsoleOutputRef.current.scrollHeight);
                }
            }, 0);
        },
        onClose: (reconnect, userdata) => {
            if (userdata.hasErrors) {
                return;
            }

            setFlashStart(false);
            setFlashStatus("done");
        },
        onError: (error, recconect, userdata) => {
            setFlashStart(false);
            setFlashStatus("error");
            userdata.hasErrors = true;
        },
        onFatal: (userdata) => {
            setFlashStart(false);
            setFlashStatus("error");
            userdata.hasErrors = true;
        },
        customEvents: {
            dataerror: (data, userdata) => {
                setFlashStart(false);
                setFlashStatus("error");
                userdata.hasErrors = true;
            }
        }
    }, []);


    useEventSource<{ hasErrors: boolean, output: string }>(utilsEventUrl(egzotechAgentManager.getAgentWithModule("yocto-flash")!, "yocto-flash-check"), checkStart, {
        onOpen: (userdata) => {
            userdata.hasErrors = false;
            userdata.output = "";
            setCheckConsoleOutput(userdata.output);

            setCheckStatus("checking");
        },
        onMessage: (data, userdata) => {
            let output = userdata.output + data.replace(/(\\n)/gm, "\n").replace(/(\\r)/gm, "\r");
            output = output.replace(/\r[^\n\r]+\r/, '\r');
            userdata.output = output;
            setCheckConsoleOutput(userdata.output);

            setTimeout(() => {
                if (checkConsoleOutputRef.current) {
                    checkConsoleOutputRef.current.scrollTo(0, checkConsoleOutputRef.current.scrollHeight);
                }
            }, 0);
        },
        onClose: (reconnect, userdata) => {
            if (userdata.hasErrors) {
                return;
            }

            setCheckStart(false);
            setCheckStatus("done");

            setTimeout(() => {
                document.querySelector('.sidra-leg-flash-page .step-3')!.scrollIntoView({
                    block: "start",
                    behavior: 'smooth'
                });
            }, 500);
        },
        onError: (error, recconect, userdata) => {
            setCheckStart(false);
            setCheckStatus("error");
            userdata.hasErrors = true;
        },
        onFatal: (userdata) => {
            setCheckStart(false);
            setCheckStatus("error");
            userdata.hasErrors = true;
        },
        customEvents: {
            dataerror: (data, userdata) => {
                setCheckStart(false);
                setCheckStatus("error");
                userdata.hasErrors = true;
            }
        }
    }, []);

    return <div class="sidra-leg-tablet-flash-page">
        <div class="stepper">
            <div class="steps">
                <div class={"step" + (flashStatus === "done" ? " done success" : " current")} data-index="1" onDblClick={() => setFlashStatus("done")}>Zaprogramuj tablet</div>
            </div>
        </div>
        <div class="info paper">
            <div>
                <h1>Informacje o Tablecie</h1>
                {flashStatus !== "done" && <Alert type="info">Wsadź karte micro SD, oraz umieść zworke w pozycji BOOT. Podłącz układ tabletu do komputera poprzez kabel micro USB i rozpocznij programowanie</Alert>}
            </div>

            <div style="margin-top: 10px">
                <button type="button" class={flashStatus !== "init" && flashStatus !== "error" ? "disabled" : ""} onClick={flashTablet}>Rozpocznij programowanie</button>
            </div>
        </div>
        {flashStatus !== "init" ? <div class="paper flash">
            <h1>Programowanie Tabletu</h1>
            <div>
                {flashStatus === "error" ? <Alert type="error">Błąd podczas programowania Tabletu</Alert> :
                    flashStatus === "done" ? <Alert type="success">Tablet został zaprogramowany</Alert> :
                    flashStatus === "programming" ? <span style="display: flex; align-items: center"><Alert type="info">Programowanie Tabletu...</Alert><span style="margin-left: 10px" class="loader"/></span> :
                    null}
            </div>
            <div class="console-output-container">
                <div>Informacje z programatora</div>
                <div ref={flashConsoleOutputRef} class="console-output">{parse(flashConsoleOutput).spans.map(v => <span style={v.css}>
                    {v.text}
                </span>)}</div>
            </div>
        </div> : null}
        {flashStatus === "done" ? <>
            <div class="stepper">
                <div class="steps">
                    <div class={"step" + (checkStatus === "done" ? " done success" : " current")} data-index="2" onDblClick={() => setCheckStatus("done")}>Sprawdź instalacje</div>
                </div>
            </div>
            <div class="info paper">
                <div>
                    <h1>Instrukcje</h1>
                    {checkStatus !== "done" && <Alert type="info">Odłącz zasilanie. Wyciągnij karte micro SD, oraz usuń zworkę. Podłącz zasilanie z powrotem, odczekaj około 10 sekund, a następnie rozpocznij sprawdzenie.</Alert>}
                </div>

                <div style="margin-top: 10px">
                    <button type="button" class={checkStatus !== "init" && checkStatus !== "error" ? "disabled" : ""} onClick={checkTablet}>Sprawdź instalacje</button>
                </div>
            </div>
            {checkStatus !== "init" && <div class="paper flash">
                <h1>Sprawdzanie instalacji Tabletu</h1>
                <div>
                    {checkStatus === "error" ? <Alert type="error">Tablet nie został poprawnie zaprogramowany, powtórz proces programowania.</Alert> :
                        checkStatus === "done" ? <Alert type="success">Tablet został zaprogramowany poprawnie.</Alert> :
                        checkStatus === "checking" ? <span style="display: flex; align-items: center"><Alert type="info">Sprawdzanie instalacji Tabletu...</Alert><span style="margin-left: 10px" class="loader"/></span> :
                        null}
                </div>
                <div class="console-output-container">
                    <div>Informacje z procesu sprawdzania</div>
                    <div ref={checkConsoleOutputRef} class="console-output">{parse(checkConsoleOutput).spans.map(v => <span style={v.css}>
                        {v.text}
                    </span>)}</div>
                    {checkStatus === "done" && <div style="margin-top: 10px">
                        <h2>Adres IP tabletu</h2>
                        <h3>{checkConsoleOutput.match(/inet (\d+.\d+.\d+.\d+)/m)?.[1]}</h3>
                    </div>}
                </div>
            </div>}
        </>: null}
        {flashStatus === "done" && checkStatus === "done" ? <div class="finish">
            <button type="button" onClick={() => {
                route(props.exitDestination ?? "/sidra-leg/serial-number", true);
            }}>Wygeneruj naklejki dla akcesoriów</button>
        </div> : null}
    </div>;
}
